import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Gamepad2, Trophy, Zap, Users, Rocket, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isEmbedded } from "@/lib/embed";
import logo from "@/assets/logo.png";

const features = [
  { icon: Gamepad2, title: "Gaming Stations", desc: "Book elite racing sims, VR rigs & flight simulators by the hour.", color: "#ff2e97" },
  { icon: Trophy, title: "Tournaments", desc: "Compete in esports events and climb the global leaderboards.", color: "#00e5ff" },
  { icon: Zap, title: "Power Rewards", desc: "Earn points every session and redeem epic loyalty rewards.", color: "#00ff9d" },
  { icon: Users, title: "Squad Up", desc: "Connect with gamers and build your ultimate racing crew.", color: "#ffb020" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      navigate("/dashboard", { replace: true });
    } else if (isEmbedded()) {
      // Inside a website iframe we skip the marketing page and go straight to the login portal.
      navigate("/login", { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen arena-bg">
      {/* Nav */}
      <header className="flex items-center justify-between px-5 sm:px-10 py-5 max-w-7xl mx-auto">
        <img src={logo} alt="Pit Lane Lounge" className="h-32 w-auto object-contain" />
        <button onClick={() => navigate("/login")} className="neon-outline-btn px-5 py-2.5 text-sm" data-testid="landing-signin">
          Sign In
        </button>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 sm:px-10 pt-10 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-in-up">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] uppercase">
            Enter The <span className="neon-cyan-text">Arena</span>.<br />
            Own The <span className="neon-magenta-text">Podium</span>.
          </h1>
          <p className="text-slate-300 text-lg mt-6 max-w-xl">
            The most advanced gaming & esports lounge. Reserve premium stations, dominate tournaments,
            and unlock rewards — all from one command center.
          </p>
          <div className="flex flex-wrap gap-4 mt-9">
            <button onClick={() => navigate("/register")} className="neon-btn px-8 py-4 text-base" data-testid="landing-join">
              <Rocket className="w-5 h-5" /> Join The Arena
            </button>
            <button onClick={() => navigate("/login")} className="neon-outline-btn px-8 py-4 text-base" data-testid="landing-login">
              Already a Gamer? <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/book")}
              data-testid="landing-book-guest"
              className="px-6 py-4 rounded-xl font-display font-bold uppercase tracking-widest text-sm transition-all inline-flex items-center gap-2"
              style={{
                background: "rgba(8,11,22,0.6)",
                border: "1px dashed rgba(148,163,184,0.45)",
                color: "#cbd5e1",
              }}
            >
              Book Without An Account
            </button>
          </div>
          <div className="mt-10 flex items-center gap-3 text-slate-300">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(0,255,157,0.14)", border: "1px solid rgba(0,255,157,0.4)" }}>
              <Zap className="w-6 h-6" style={{ color: "#00ff9d" }} />
            </div>
            <p><span className="font-display font-bold text-white text-lg">1,000 points</span> welcome bonus on sign up.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 animate-in-up">
          {features.map((f) => (
            <div key={f.title} className="glass glass-hover rounded-2xl p-6">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: `${f.color}1f`, border: `1px solid ${f.color}66` }}>
                <f.icon className="w-7 h-7" style={{ color: f.color }} />
              </div>
              <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide mb-2">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
